import { useEffect, useRef, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Spinner, Button } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../helpers/api';
import { useNavigate } from 'react-router-dom';
import { formatCamelCase, formatCreatedDate } from '../constants/utils';
import PermissionGuard from '../components/Common/PermissionGuard';

const Forms = () => {
    document.title = "Requests | ArcMedix";
    const navigate = useNavigate();
    const [organizations, setOrganizations] = useState(null);
    const [assets, setAssets] = useState(null);
    const [assetId, setAssetId] = useState('');
    const [organizationId, setOrganizationId] = useState('');
    const [forms, setForms] = useState([]);
    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const tableRef = useRef(null);

    const fetchOrganizations = async() => {
        const {data} = await api.get('organization/all');
        setOrganizations(data.response);
        if (data.response && data.response.length > 0) {
            setOrganizationId(data.response[0].id);
        }
    };

    const fetchAssets = async() => {
        const {data} = await api.get(`asset/get-by-organization?organizationId=${organizationId}&count=9999`);
        setAssets(data.response.assets);
    };

    const fetchforms = useCallback(async () => {
        if (!organizationId) return;
        
        setLoading(true);
        try {
            const {data} = await api.get(`request-all?organizationId=${organizationId}&count=${count}&assetId=${assetId}`);
            
            // Transform the response to add type identifiers
            const transformedResponse = Object.entries(data.response).reduce((acc, [key, value]) => {
                if (key.endsWith('Responses') || key === 'productTrainings') {
                    const type = key.replace('Responses', '').replace('product', '');
                    const typedItems = value.map(item => ({
                        ...item,
                        responseType: type
                    }));
                    return [...acc, ...typedItems];
                }
                return acc;
            }, []);
            
            setForms(transformedResponse || []);
            // Assume no more data if we get fewer items than requested
            setHasMore(transformedResponse.length >= count);
        } catch (error) {
            console.error('Error fetching forms:', error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [organizationId, count, assetId]);

    const handleLoadMore = () => {
        setCount(prevCount => prevCount + 10);
    };

    const handleViewClick = (formKey, formData) => {
        let route = '';
        switch(formKey) {
            case 'amc':
                route = '/amc-details';
                break;
            case 'calibration':
                route = '/calibration-details';
                break;
            case 'catalogue':
                route = '/catalogue-details';
                break;
            case 'compatibilityCheck':
                route = '/compatibility-check-details';
                break;
            case 'equipmentUpgrade':
                route = '/equipment-upgrade-details';
                break;
            case 'installation':
                route = '/installation-details';
                break;
            case 'preventionMaintanance':
                route = '/prevention-maintenance-details';
                break;
            case 'Trainings':
                route = '/product-training-details';
                break;
            case 'spareParts':
                route = '/spare-parts-details';
                break;
            default:
                route = '/request-detail';
        }

        navigate(route, {
            state: {
                assetId,
                organizationId,
                formData
            }
        });
    };

    const handleOrganizationChange = (e) => {
        setOrganizationId(e.target.value);
        setForms([]);
        setCount(10);
        setHasMore(true);
    };

    const handleAssetChange = (e) => {
        const value = e.target.value;
        setAssetId(value === 'all' ? "" : value);
        setForms([]);
        setCount(10);
        setHasMore(true);
    };

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        if(organizationId)
            fetchAssets();
    },[organizationId]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchforms();
        }, 100);
        
        return () => clearTimeout(timer);
    }, [fetchforms]);

    return (
        <div className='page-content mt-4'>            
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">Requests</h3>
                        <div className="d-flex align-items-center gap-2">
                            <select 
                                className="form-select form-select-md w-auto" 
                                value={organizationId || ''}
                                onChange={handleOrganizationChange}
                            >
                                <option value="" disabled>Select Organization</option>
                                {organizations?.map((organization) => (
                                    <option key={organization.id} value={organization.id}>
                                        {organization?.name}
                                    </option>
                                ))}
                            </select>
                            <select 
                                className="form-select form-select-md w-auto" 
                                value={assetId || ''}
                                onChange={handleAssetChange}
                            >
                                <option value="placeholder" disabled>Select Asset</option>
                                <option value="all">All Assets</option>
                                {assets?.map((asset) => (
                                    <option key={asset?.id} value={asset?.id}>
                                        {asset?.assetName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </CardHeader>
                
                <CardBody>
                    <div 
                        id="scrollableDiv"
                        style={{ 
                            overflowX: 'auto',
                            maxHeight: 'calc(100vh - 300px)',
                        }}
                        ref={tableRef}
                    >
                        <table className="table table-hover mb-0">
                            <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Asset Name</th>
                                    <th>Location</th>
                                    <th>Created At</th>
                                    <th style={{ width: '100px', textAlign: 'center' }}>View</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                    {forms?.map((form, ind) => (
                                        <motion.tr
                                            key={ind}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <th scope="row" style={{ textAlign: 'center', verticalAlign: 'middle' }}>{ind + 1}</th>
                                            <td style={{ verticalAlign: 'middle' }}>{form?.responseType === 'Trainings'? 'Product Training' : formatCamelCase(form?.responseType)}</td>
                                            <td style={{ verticalAlign: 'middle' }}>{form?.formStatus}</td>
                                            <td style={{ verticalAlign: 'middle' }}>{form?.asset?.assetName}</td>
                                            <td style={{ verticalAlign: 'middle' }}>{form?.asset?.currentLocation}</td>
                                            <td style={{ verticalAlign: 'middle' }}>{formatCreatedDate(form?.createdAt)}</td>
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                <PermissionGuard permission="enum:read">
                                                    <motion.div
                                                        whileHover={{ scale: 1.2 }}
                                                        whileTap={{ scale: 0.9 }}
                                                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'rgba(40, 167, 69, 0.1)',
                                                        }}
                                                        onClick={() => handleViewClick(form?.responseType,form)}
                                                    >
                                                        <motion.svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="#28a745"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            initial={{ x: 0 }}
                                                            animate={{ x: [0, 2, 0] }}
                                                            transition={{
                                                                repeat: Infinity,
                                                                duration: 1.5,
                                                                ease: "easeInOut"
                                                            }}
                                                        >
                                                            <path d="M5 12h14" />
                                                            <path d="M12 5l7 7-7 7" />
                                                        </motion.svg>
                                                    </motion.div>
                                                </PermissionGuard>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Load More Button */}
                    <div className="text-center mt-3">
                        {loading ? (
                            <Spinner color="primary" />
                        ) : hasMore ? (
                            <Button 
                                color="primary" 
                                onClick={handleLoadMore}
                                disabled={loading}
                            >
                                Load More
                            </Button>
                        ) : (
                            forms.length > 0 && (
                                <div className="text-muted py-2">
                                    No more work orders to load
                                </div>
                            )
                        )}
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default Forms;